import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { readAlpsPillars } from "../constants/alps";
import styles from "../styles/AlpsSignature.module.scss";

/**
 * Oversized typographic treatment of the four ALPS pillars.
 *
 * The words are read straight out of the approved copy
 * ("ALPS significa Ascolta, Leggi, Parla, Scrivi.") so this stays a
 * visualisation of existing content rather than new wording.
 *
 * On desktop the band pins to the top of the method block while the seven
 * principles scroll past it, and compresses once pinned so it never eats the
 * viewport. A sentinel above the band reports the pinned state.
 */
const AlpsSignature = ({ onStuckChange }) => {
  const reduceMotion = useReducedMotion();
  const pillars = readAlpsPillars();
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return undefined;

    const navOffset =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--nav-h")
      ) * 16 || 72;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const stuck = !entry.isIntersecting;
        setIsStuck(stuck);
        onStuckChange?.(stuck);
      },
      { rootMargin: `-${Math.round(navOffset) + 1}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onStuckChange]);

  if (pillars.length === 0) return null;

  return (
    <>
      <span ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />

      <div className={`${styles.band} ${isStuck ? styles.isStuck : ""}`}>
        <ol className={styles.grid}>
          {pillars.map((word, index) => {
            const initial = word.charAt(0);
            const rest = word.slice(1);

            return (
              <motion.li
                key={word}
                className={styles.pillar}
                data-tone={index % 4}
                initial={reduceMotion ? false : { opacity: 0, y: 22 }}
                whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.09,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <span className={styles.word}>
                  <span className={styles.initial} aria-hidden="true">
                    {initial}
                  </span>
                  <span className={styles.rest} aria-hidden="true">
                    {rest}
                  </span>
                  <span className="sr-only">{word}</span>
                </span>
                <span className={styles.rule} aria-hidden="true" />
              </motion.li>
            );
          })}
        </ol>
      </div>
    </>
  );
};

export default AlpsSignature;
